import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Building2, ClipboardList, Loader2, Printer } from 'lucide-react'
import { ordersService } from '../services/ordersService'
import { STATUS_LABEL, STATUS_STYLE } from '../constants/orderStatus'
import { useSelectedPropertyId, useSelectedPropertyType } from '../store/propertyStore'
import type { Order, OrderStatus } from '../types/api.types'
import { AdminBanner } from '../features/admin/components/AdminBanner'
import { EmptyState } from '../features/admin/components/EmptyState'

const POLL_MS = 10_000
// Handoff §8: an unprinted ticket older than this means the printer is down,
// not that the kitchen is quick.
const PRINTER_GRACE_MS = 60_000

// Handoff §5. Skipping forward is allowed, going back is a 409, and the last
// entry is terminal.
const FLOW: OrderStatus[] = ['PLACED', 'IN_KITCHEN', 'READY', 'SERVED', 'BILLING', 'COMPLETED']
const CANCELLABLE: OrderStatus[] = ['PLACED', 'IN_KITCHEN', 'READY']

// The verb for moving an order on, not a description of the state it leaves.
const ADVANCE_LABEL: Partial<Record<OrderStatus, string>> = {
  PLACED: 'Start cooking',
  IN_KITCHEN: 'Mark ready',
  READY: 'Mark served',
  SERVED: 'Send to billing',
  BILLING: 'Complete',
}

type View = 'ACTIVE' | 'ALL' | OrderStatus

const VIEWS: { value: View; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PLACED', label: 'Placed' },
  { value: 'IN_KITCHEN', label: 'In kitchen' },
  { value: 'READY', label: 'Ready' },
  { value: 'SERVED', label: 'Served' },
  { value: 'BILLING', label: 'Billing' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'ALL', label: 'All' },
]

function nextStatus(status: OrderStatus): OrderStatus | null {
  const i = FLOW.indexOf(status)
  return i >= 0 && i < FLOW.length - 1 ? FLOW[i + 1] : null
}

function isActive(order: Order): boolean {
  return order.status !== 'COMPLETED' && order.status !== 'CANCELLED'
}

function matchesView(order: Order, view: View): boolean {
  if (view === 'ALL') return true
  if (view === 'ACTIVE') return isActive(order)
  return order.status === view
}

function ago(iso: string, now: number): string {
  const seconds = Math.max(0, Math.round((now - Date.parse(iso)) / 1000))
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

// A ticket the kitchen never got, old enough that a working printer would have
// produced it by now.
function printerLooksDown(order: Order, now: number): boolean {
  return order.printed_at == null
    && isActive(order)
    && now - Date.parse(order.created_at) > PRINTER_GRACE_MS
}

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback
}

export function OrdersPage(): JSX.Element {
  const propertyId = useSelectedPropertyId()
  const propertyType = useSelectedPropertyType()

  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [view, setView] = useState<View>('ACTIVE')
  const [unprintedOnly, setUnprintedOnly] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [confirmCancel, setConfirmCancel] = useState<Order | null>(null)
  const [now, setNow] = useState(() => Date.now())

  // Polling must not blank the board or fight a tap in progress.
  const busyRef = useRef<number | null>(null)
  busyRef.current = busyId

  useEffect(() => { if (success) { const t = setTimeout(() => setSuccess(null), 5000); return () => clearTimeout(t) } }, [success])
  useEffect(() => { if (error) { const t = setTimeout(() => setError(null), 8000); return () => clearTimeout(t) } }, [error])

  // ponytail: one unfiltered fetch, status filtered client-side — a kitchen
  // display needs PLACED and IN_KITCHEN together and ?status= takes one value.
  // Move to a fetch per column if 100 newest stops covering the active set.
  const refresh = useCallback(async (silent: boolean) => {
    if (propertyId == null) { setOrders([]); setLoading(false); return }
    if (!silent) setLoading(true)
    try {
      setOrders(await ordersService.list(propertyId, { unprintedOnly }))
      setNow(Date.now())
    } catch (e) {
      if (!silent) setError(errorMessage(e, 'Failed to load orders'))
    } finally {
      if (!silent) setLoading(false)
    }
  }, [propertyId, unprintedOnly])

  useEffect(() => { refresh(false) }, [refresh])

  useEffect(() => {
    const id = setInterval(() => { if (busyRef.current == null) refresh(true) }, POLL_MS)
    return () => clearInterval(id)
  }, [refresh])

  const visible = useMemo(() => orders.filter(o => matchesView(o, view)), [orders, view])
  const counts = useMemo(() => {
    const map = new Map<View, number>()
    map.set('ALL', orders.length)
    map.set('ACTIVE', orders.filter(isActive).length)
    for (const order of orders) map.set(order.status, (map.get(order.status) ?? 0) + 1)
    return map
  }, [orders])

  const stuckTickets = orders.filter(o => printerLooksDown(o, now)).length

  // The server returns the updated order, so patch it in place rather than
  // refetching the board out from under whoever is tapping.
  function replace(updated: Order) {
    setOrders(prev => prev.map(o => (o.id === updated.id ? updated : o)))
  }

  async function advance(order: Order) {
    const target = nextStatus(order.status)
    if (!target) return
    setBusyId(order.id)
    try {
      replace(await ordersService.setStatus(order.id, target))
      // §5: READY fires a fire-and-forget announcement. A 200 is not proof the
      // customer heard anything, so this never claims the table was told.
      setSuccess(`Table ${order.table_number ?? '—'} → ${STATUS_LABEL[target].toLowerCase()}`)
    } catch (e) {
      setError(errorMessage(e, 'Could not move this order along'))
    } finally {
      setBusyId(null)
    }
  }

  async function cancel(order: Order) {
    setConfirmCancel(null)
    setBusyId(order.id)
    try {
      replace(await ordersService.cancel(order.id))
      setSuccess(`Order for table ${order.table_number ?? '—'} cancelled`)
    } catch (e) {
      setError(errorMessage(e, 'Could not cancel this order'))
    } finally {
      setBusyId(null)
    }
  }

  if (propertyId == null) {
    return (
      <EmptyState
        icon={Building2}
        message="Select a property"
        hint="Pick one from the bar at the top to see its orders."
      />
    )
  }

  if (propertyType !== 'RESTAURANT') {
    return (
      <EmptyState
        icon={ClipboardList}
        message="Select a restaurant"
        hint="Orders belong to restaurant properties. Switch property from the bar above."
      />
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-[20px] font-bold text-gray-900">Orders</h1>
          <p className="text-[13px] text-gray-500">
            What the tables asked for, and where each order has got to. Refreshes every 10 seconds.
          </p>
        </div>
        <label className="flex items-center gap-2 self-start text-[13px] text-gray-700 sm:self-auto">
          <input
            type="checkbox"
            checked={unprintedOnly}
            onChange={e => setUnprintedOnly(e.target.checked)}
            className="h-4 w-4 accent-brand-green"
          />
          Unprinted only
        </label>
      </div>

      <AdminBanner message={error} variant="error" />
      <AdminBanner message={success} variant="success" />

      {stuckTickets > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800">
          <Printer size={16} className="mt-px shrink-0" />
          <span>
            {stuckTickets} {stuckTickets === 1 ? 'ticket' : 'tickets'} never reached the kitchen
            printer. Check the printer — the kitchen may not know about{' '}
            {stuckTickets === 1 ? 'this order' : 'these orders'}.
          </span>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {VIEWS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            onClick={() => setView(value)}
            className={`rounded-full px-3 py-1.5 text-[13px] transition-colors ${
              view === value
                ? 'bg-brand-green font-semibold text-black'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {label} <span className="opacity-60">{counts.get(value) ?? 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-brand-green" />
          <span className="ml-2 text-sm text-gray-500">Loading orders...</span>
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          message={orders.length === 0 ? 'No orders yet' : 'Nothing in this view'}
          hint={orders.length === 0
            ? 'Orders placed at the tables land here'
            : 'Try another status, or clear the unprinted filter'}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {visible.map(order => {
            const target = nextStatus(order.status)
            const busy = busyId === order.id
            return (
              <div
                key={order.id}
                className="flex min-w-0 flex-col rounded-lg border border-gray-200 bg-white p-5 transition-all duration-200 hover:shadow-md"
              >
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="truncate text-[15px] font-semibold text-gray-900">
                      Table {order.table_number ?? '—'}
                    </h3>
                    <p className="text-[12px] text-gray-400">
                      #{order.id} · {ago(order.created_at, now)} ago
                    </p>
                  </div>
                  <span className={`shrink-0 rounded px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[order.status]}`}>
                    {STATUS_LABEL[order.status]}
                  </span>
                </div>

                <ul className="mb-3 space-y-1.5 text-[13px]">
                  {order.items.map((item, i) => (
                    <li key={`${order.id}-${i}`} className="flex gap-2">
                      <span className="shrink-0 font-medium text-gray-500">{item.quantity}×</span>
                      <span className="min-w-0 flex-1 text-gray-800">{item.name}</span>
                      <span className="shrink-0 tabular-nums text-gray-500">{item.line_total}</span>
                    </li>
                  ))}
                </ul>

                {order.notes && (
                  <p className="mb-3 text-[12px] italic text-amber-700">{order.notes}</p>
                )}

                <div className="mb-3 flex items-center justify-between border-t border-gray-100 pt-2 text-[13px]">
                  <span className="text-gray-400">Total</span>
                  <span className="font-semibold text-gray-900">
                    {order.currency} {order.total_amount}
                  </span>
                </div>

                {printerLooksDown(order, now) && (
                  <p className="mb-3 flex items-center gap-1.5 rounded bg-amber-50 px-2 py-1 text-[12px] text-amber-800">
                    <AlertTriangle size={12} className="shrink-0" /> Never printed
                  </p>
                )}

                <div className="mt-auto flex gap-2">
                  {target && (
                    <button
                      type="button"
                      onClick={() => advance(order)}
                      disabled={busy}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-green py-2.5 text-[13px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
                    >
                      {busy && <Loader2 size={14} className="animate-spin" />}
                      {ADVANCE_LABEL[order.status] ?? STATUS_LABEL[target]}
                    </button>
                  )}
                  {CANCELLABLE.includes(order.status) && (
                    <button
                      type="button"
                      onClick={() => setConfirmCancel(order)}
                      disabled={busy}
                      className="rounded-lg border border-gray-300 px-3 py-2.5 text-[13px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {confirmCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6">
            <h2 className="text-lg font-bold text-gray-900">
              Cancel the order for table {confirmCancel.table_number ?? '—'}?
            </h2>
            <p className="mt-2 text-[13px] text-gray-600">
              The order stays on the books as cancelled so the day&apos;s takings still add up. It
              cannot be reopened — a corrected order is a new one.
            </p>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:gap-3">
              <button
                type="button"
                onClick={() => cancel(confirmCancel)}
                className="flex-1 rounded-lg bg-red-600 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90"
              >
                Cancel the order
              </button>
              <button
                type="button"
                onClick={() => setConfirmCancel(null)}
                className="rounded-lg border border-gray-300 px-6 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50"
              >
                Keep it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
